import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { Users, Plus, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RoomsLaunch() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [passage, setPassage] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/rooms", { title: title.trim(), passage_ref: passage.trim() });
      nav(`/rooms/${data.code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const join = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy("join");
    try {
      await api.get(`/rooms/${c}`);
      nav(`/rooms/${c}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-8" data-testid="rooms-launch">
      <div>
        <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-2">
          <Users className="w-4 h-4" strokeWidth={1.8} /> Study together
        </div>
        <h1 className="text-3xl text-white">Live study rooms</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-lg">
          Open the Word with others in real time — shared notes, live chat, and an optional study companion the host can turn on for the group.
        </p>
      </div>

      <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-6 space-y-4">
        <h2 className="text-base font-semibold text-white flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#00E5FF]" strokeWidth={2} /> Start a room
        </h2>
        <input
          data-testid="room-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Room name (e.g. Tuesday small group)"
          className="w-full text-sm border border-[#29344D] rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30"
        />
        <input
          data-testid="room-passage-input"
          value={passage}
          onChange={(e) => setPassage(e.target.value)}
          placeholder="Passage to open (optional, e.g. Psalm 23)"
          className="w-full text-sm border border-[#29344D] rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30"
        />
        <button
          data-testid="room-create-btn"
          onClick={create}
          disabled={busy === "create"}
          className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#07090E] text-sm font-semibold px-5 py-2.5 hover:bg-[#00B8CC] disabled:opacity-60"
        >
          {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create & host
        </button>
      </div>

      <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-6 space-y-4">
        <h2 className="text-base font-semibold text-white flex items-center gap-2">
          <LogIn className="w-4 h-4 text-[#00E5FF]" strokeWidth={2} /> Join a room
        </h2>
        <div className="flex gap-2">
          <input
            data-testid="room-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Enter room code (e.g. AE82DE)"
            className="flex-1 text-sm border border-[#29344D] rounded-xl px-3.5 py-2.5 tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30"
          />
          <button
            data-testid="room-join-btn"
            onClick={join}
            disabled={busy === "join"}
            className="inline-flex items-center gap-2 rounded-full border border-[#29344D] text-slate-300 text-sm font-semibold px-5 py-2.5 hover:bg-[#0B0F16] disabled:opacity-60"
          >
            {busy === "join" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
          </button>
        </div>
      </div>
    </div>
  );
}
